import React, { useState, useEffect, useRef } from 'react';
import { Select, Button, Checkbox, Table, Input, message, Icon } from 'antd';
import axios from 'axios';
import { BASE_URL } from '../../middleware/api';
import './DomainManagementPage.css';

const { Option } = Select;

const DomainManagementPage = () => {
  const [domainName, setDomainName] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [showConfirmed, setShowConfirmed] = useState(false);
  const [exactMatch, setExactMatch] = useState(false);
  const [facebook, setFacebook] = useState(false);
  const [facebookExcluded, setFacebookExcluded] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(new Set());
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [bulkStatus, setBulkStatus] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  const [countriesList, setCountriesList] = useState([]);
  const [countryFilter, setCountryFilter] = useState({ value: [] });
  const [countryDraft, setCountryDraft] = useState({ value: [] });
  const [scrapedCountryFilter, setScrapedCountryFilter] = useState({
    value: []
  });
  const [scrapedCountryDraft, setScrapedCountryDraft] = useState({ value: [] });

  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const domainNameRef = useRef(domainName);
  domainNameRef.current = domainName;

  const selectedStatusRef = useRef(selectedStatus);
  selectedStatusRef.current = selectedStatus;

  const showConfirmedRef = useRef(showConfirmed);
  showConfirmedRef.current = showConfirmed;

  const exactMatchRef = useRef(exactMatch);
  exactMatchRef.current = exactMatch;

  const facebookRef = useRef(facebook);
  facebookRef.current = facebook;

  const facebookExcludedRef = useRef(facebookExcluded);
  facebookExcludedRef.current = facebookExcluded;

  const countryFilterRef = useRef(countryFilter);
  countryFilterRef.current = countryFilter;

  const scrapedCountryFilterRef = useRef(scrapedCountryFilter);
  scrapedCountryFilterRef.current = scrapedCountryFilter;

  const buildSearchParams = (overrides = {}) => {
    const params = {};

    if (domainNameRef.current && domainNameRef.current.trim()) {
      params.domain = domainNameRef.current.trim();
    }

    if (
      selectedStatusRef.current !== null &&
      selectedStatusRef.current !== undefined
    ) {
      params.blacklisted = selectedStatusRef.current;
    }

    params.confirmed = showConfirmedRef.current;

    if (exactMatchRef.current) {
      params.exactMatch = true;
    }

    if (facebookRef.current) {
      params.facebook = true;
    } else if (facebookExcludedRef.current) {
      params.facebook = false;
    }

    if (countryFilterRef.current.value.length) {
      params.country = countryFilterRef.current.value.join(',');
    }

    if (scrapedCountryFilterRef.current.value.length) {
      params.scrapedCountry = scrapedCountryFilterRef.current.value.join(',');
    }

    return { ...params, ...overrides };
  };

  const searchDomains = async (params = {}) => {
    setLoading(true);

    const queryString = new URLSearchParams(params).toString();
    const url = `${BASE_URL}/blacklisted_domain${
      queryString ? `?${queryString}` : ''
    }`;

    try {
      const response = await axios.get(url);
      const data = response.data;

      if (data && data.content) {
        const transformedData = data.content.map((item) => ({
          key: item.id,
          id: item.id,
          domain: item.domain,
          sightingsCount: item.sightingsCount,
          blacklisted: item.blacklisted,
          confirmed: item.blacklistedConfirmed,
          blacklistedAt: item.blacklistedAt,
          blacklistedConfirmedAt: item.blacklistedConfirmedAt,
          country: item.country,
          scrapedCountry: item.scrapedCountry
        }));

        setTableData(transformedData);
        setPagination((prev) => ({
          ...prev,
          total: data.totalCount ?? data.count ?? 0,
          current: (data.pageNumber ?? 0) + 1
        }));
      }
    } catch (error) {
      console.error('Search failed:', error);
      message.error('Failed to load domains. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    searchDomains({ confirmed: false, page: 0, size: 20 });

    axios
      .get(`${BASE_URL}/assessment_domain/countries_list`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [res.data];
        setCountriesList(data.map((c) => c.fullName || c.alpha3Code || c));
      })
      .catch((e) => console.error('Failed to load countries:', e));
  }, []);

  const handleSearch = () => {
    const size = paginationRef.current.pageSize;
    const params = buildSearchParams({ page: 0, size });
    setPagination((prev) => ({ ...prev, current: 1 }));
    searchDomains(params);
  };

  const handleClearFilters = () => {
    setDomainName('');
    setSelectedStatus(null);
    setShowConfirmed(false);
    setExactMatch(false);
    setFacebook(false);
    setFacebookExcluded(false);
    setSelectedKeys([]);
    const empty = { value: [] };
    setCountryFilter(empty);
    setCountryDraft(empty);
    setScrapedCountryFilter(empty);
    setScrapedCountryDraft(empty);
    const size = paginationRef.current.pageSize;
    setPagination((prev) => ({ ...prev, current: 1 }));
    searchDomains({ confirmed: false, page: 0, size });
  };

  const handleTableChange = (newPagination) => {
    const page = newPagination.current - 1;
    const size = newPagination.pageSize;

    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: size
    }));

    const params = buildSearchParams({ page, size });
    searchDomains(params);
  };

  const handleBlacklistedChange = async (value, record) => {
    setUpdatingStatus((prev) => new Set(prev).add(record.key));
    try {
      await axios.put(
        `${BASE_URL}/blacklisted_domain/${record.id}/blacklisted?blacklisted=${value}`
      );
      message.success(
        `Status updated to ${
          value ? 'Blacklisted' : 'Not blacklisted'
        } successfully.`
      );
      const { current, pageSize } = paginationRef.current;
      await searchDomains(
        buildSearchParams({ page: current - 1, size: pageSize })
      );
    } catch (error) {
      message.error('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus((prev) => {
        const next = new Set(prev);
        next.delete(record.key);
        return next;
      });
    }
  };

  const handleConfirmedChange = async (checked, record) => {
    setUpdatingStatus((prev) => new Set(prev).add(record.key));
    try {
      await axios.put(
        `${BASE_URL}/blacklisted_domain/${record.id}/confirmed?confirmed=${checked}`
      );
      message.success(
        `Domain marked as ${
          checked ? 'confirmed' : 'unconfirmed'
        } successfully.`
      );
      const { current, pageSize } = paginationRef.current;
      await searchDomains(
        buildSearchParams({ page: current - 1, size: pageSize })
      );
    } catch (error) {
      message.error('Failed to update confirmation status. Please try again.');
    } finally {
      setUpdatingStatus((prev) => {
        const next = new Set(prev);
        next.delete(record.key);
        return next;
      });
    }
  };

  const handleCountryChange = async (value, record) => {
    setUpdatingStatus((prev) => new Set(prev).add(record.key));
    try {
      await axios.put(`${BASE_URL}/blacklisted_domain/${record.id}/country`, {
        country: value || null
      });
      message.success('Country updated successfully.');
    } catch (error) {
      message.error('Failed to update country. Please try again.');
    } finally {
      setUpdatingStatus((prev) => {
        const next = new Set(prev);
        next.delete(record.key);
        return next;
      });
      const { current, pageSize } = paginationRef.current;
      setTimeout(() => {
        searchDomains(buildSearchParams({ page: current - 1, size: pageSize }));
      }, 1000);
    }
  };

  const handleScrapedCountryChange = async (value, record) => {
    setUpdatingStatus((prev) => new Set(prev).add(record.key));
    try {
      await axios.put(
        `${BASE_URL}/blacklisted_domain/${record.id}/scraped_country`,
        { country: value || null }
      );
      message.success('Non-verified country updated successfully.');
    } catch (error) {
      message.error('Failed to update non-verified country. Please try again.');
    } finally {
      setUpdatingStatus((prev) => {
        const next = new Set(prev);
        next.delete(record.key);
        return next;
      });
      const { current, pageSize } = paginationRef.current;
      setTimeout(() => {
        searchDomains(buildSearchParams({ page: current - 1, size: pageSize }));
      }, 1000);
    }
  };

  const handleBulkUpdate = async () => {
    if (!selectedKeys.length || bulkStatus === null) return;
    setBulkUpdating(true);
    try {
      await Promise.all(
        selectedKeys.map((key) =>
          axios.put(
            `${BASE_URL}/blacklisted_domain/${key}/blacklisted?blacklisted=${bulkStatus}`
          )
        )
      );
      message.success(
        `${selectedKeys.length} domain(s) updated to ${
          bulkStatus ? 'Blacklisted' : 'Not blacklisted'
        }.`
      );
      setSelectedKeys([]);
      setBulkStatus(null);
    } catch (error) {
      message.error('Failed to update some domains. Please try again.');
    } finally {
      setBulkUpdating(false);
      const { current, pageSize } = paginationRef.current;
      setTimeout(() => {
        searchDomains(buildSearchParams({ page: current - 1, size: pageSize }));
      }, 1000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const makeFilterDropdown =
    ({ draft, setDraft, filter, setFilter, options, onConfirm }) =>
    ({ confirm }) =>
      (
        <div style={{ padding: 8 }} onMouseDown={(e) => e.stopPropagation()}>
          <Select
            value={draft.value}
            mode='multiple'
            style={{ width: 165, marginBottom: 8, display: 'block' }}
            showSearch
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .startsWith(input.toLowerCase())
            }
            onChange={(val) => setDraft((d) => ({ ...d, value: val }))}
            allowClear
            placeholder='Search...'
          >
            {options.map((opt) => (
              <Option key={opt} value={opt}>
                {opt}
              </Option>
            ))}
          </Select>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button
              type='primary'
              size='small'
              onClick={() => {
                const applied = { ...draft };
                setFilter(applied);
                onConfirm(applied);
                confirm();
              }}
            >
              Filter
            </Button>
            <Button
              size='small'
              onClick={() => {
                const empty = { value: [] };
                setDraft(empty);
                setFilter(empty);
                onConfirm(empty);
                confirm();
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      );

  const columns = [
    {
      title: 'Domain name',
      dataIndex: 'domain',
      key: 'domain',
      width: 220
    },
    {
      title: 'Number of sightings',
      dataIndex: 'sightingsCount',
      key: 'sightingsCount',
      width: 180
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 180,
      render: (country, record) => (
        <Select
          value={country || undefined}
          onChange={(value) => handleCountryChange(value, record)}
          style={{ width: '100%' }}
          size='small'
          allowClear
          showSearch
          placeholder='—'
          loading={updatingStatus.has(record.key)}
          disabled={updatingStatus.has(record.key)}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().startsWith(input.toLowerCase())
          }
        >
          {countriesList.map((c) => (
            <Option key={c} value={c}>
              {c}
            </Option>
          ))}
        </Select>
      ),
      filterDropdown: makeFilterDropdown({
        draft: countryDraft,
        setDraft: setCountryDraft,
        filter: countryFilter,
        setFilter: setCountryFilter,
        options: countriesList,
        onConfirm: (f) => {
          countryFilterRef.current = f;
          const { pageSize } = paginationRef.current;
          searchDomains(buildSearchParams({ page: 0, size: pageSize }));
          setPagination((prev) => ({ ...prev, current: 1 }));
        }
      }),
      filterIcon: () => (
        <Icon
          type='filter'
          style={{ color: countryFilter.value.length ? '#1890ff' : undefined }}
        />
      ),
      filteredValue: countryFilter.value.length ? countryFilter.value : null
    },
    {
      title: 'Non-verified Country',
      dataIndex: 'scrapedCountry',
      key: 'scrapedCountry',
      width: 200,
      render: (scrapedCountry, record) => (
        <Select
          value={scrapedCountry || undefined}
          onChange={(value) => handleScrapedCountryChange(value, record)}
          style={{ width: '100%' }}
          size='small'
          allowClear
          showSearch
          placeholder='—'
          loading={updatingStatus.has(record.key)}
          disabled={updatingStatus.has(record.key)}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().startsWith(input.toLowerCase())
          }
        >
          {countriesList.map((c) => (
            <Option key={c} value={c}>
              {c}
            </Option>
          ))}
        </Select>
      ),
      filterDropdown: makeFilterDropdown({
        draft: scrapedCountryDraft,
        setDraft: setScrapedCountryDraft,
        filter: scrapedCountryFilter,
        setFilter: setScrapedCountryFilter,
        options: countriesList,
        onConfirm: (f) => {
          scrapedCountryFilterRef.current = f;
          const { pageSize } = paginationRef.current;
          searchDomains(buildSearchParams({ page: 0, size: pageSize }));
          setPagination((prev) => ({ ...prev, current: 1 }));
        }
      }),
      filterIcon: () => (
        <Icon
          type='filter'
          style={{
            color: scrapedCountryFilter.value.length ? '#1890ff' : undefined
          }}
        />
      ),
      filteredValue: scrapedCountryFilter.value.length
        ? scrapedCountryFilter.value
        : null
    },
    {
      title: 'Domain status',
      dataIndex: 'blacklisted',
      key: 'blacklisted',
      width: 200,
      render: (blacklisted, record) => (
        <Select
          value={blacklisted ? 'blacklisted' : 'not_blacklisted'}
          onChange={(value) =>
            handleBlacklistedChange(value === 'blacklisted', record)
          }
          style={{ width: '100%' }}
          size='small'
          loading={updatingStatus.has(record.key)}
          disabled={updatingStatus.has(record.key)}
        >
          <Option value='blacklisted'>Blacklisted</Option>
          <Option value='not_blacklisted'>Not blacklisted</Option>
        </Select>
      )
    },
    {
      title: 'Confirmed',
      dataIndex: 'confirmed',
      key: 'confirmed',
      width: 140,
      render: (confirmed, record) => (
        <Checkbox
          checked={confirmed}
          onChange={(e) => handleConfirmedChange(e.target.checked, record)}
          disabled={updatingStatus.has(record.key)}
        >
          {updatingStatus.has(record.key) ? 'Updating...' : 'Confirmed'}
        </Checkbox>
      )
    },
    {
      title: 'Blacklisted at',
      dataIndex: 'blacklistedAt',
      key: 'blacklistedAt',
      width: 130,
      render: (date) => formatDate(date)
    },
    {
      title: 'Confirmed at',
      dataIndex: 'blacklistedConfirmedAt',
      key: 'blacklistedConfirmedAt',
      width: 130,
      render: (date) => formatDate(date)
    }
  ];

  return (
    <div className='domain-management-page'>
      <div className='page-content'>
        <div className='filters-section'>
          <div className='filter-item'>
            <label className='filter-label'>Domain name</label>
            <Input
              placeholder='Enter domain name'
              value={domainName}
              onChange={(e) => setDomainName(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: '200px' }}
            />
          </div>

          <div className='filter-item'>
            <label className='filter-label'>Status</label>
            <Select
              placeholder='Select Status'
              value={selectedStatus}
              onChange={(value) => setSelectedStatus(value)}
              style={{ width: '200px' }}
              allowClear
            >
              <Option value={true}>Blacklisted</Option>
              <Option value={false}>Not Blacklisted</Option>
            </Select>
          </div>

          <div className='filter-item'>
            <label className='filter-label'>Options</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div className='checkbox-wrapper'>
                <Checkbox
                  checked={showConfirmed}
                  onChange={(e) => setShowConfirmed(e.target.checked)}
                >
                  Show all confirmed
                </Checkbox>
              </div>
              <div className='checkbox-wrapper'>
                <Checkbox
                  checked={exactMatch}
                  onChange={(e) => setExactMatch(e.target.checked)}
                >
                  Exact Match
                </Checkbox>
              </div>
              <div className='checkbox-wrapper'>
                <Checkbox
                  checked={facebook}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFacebook(true);
                      setFacebookExcluded(false);
                    } else {
                      setFacebook(false);
                    }
                  }}
                >
                  Facebook
                </Checkbox>
              </div>
              <div className='checkbox-wrapper'>
                <Checkbox
                  checked={facebookExcluded}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFacebookExcluded(true);
                      setFacebook(false);
                    } else {
                      setFacebookExcluded(false);
                    }
                  }}
                >
                  Facebook excluded
                </Checkbox>
              </div>
            </div>
          </div>

          <div className='filter-item'>
            <label className='filter-label' style={{ visibility: 'hidden' }}>
              Actions
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                type='primary'
                onClick={handleSearch}
                style={{ flex: 1 }}
                loading={loading}
              >
                Search
              </Button>
              <Button onClick={handleClearFilters} style={{ flex: 1 }}>
                Clear
              </Button>
            </div>
          </div>
        </div>

        <div className='content-section'>
          {selectedKeys.length > 0 && (
            <div className='bulk-action-bar'>
              <span className='bulk-action-bar__count'>
                {selectedKeys.length} domain(s) selected
              </span>
              <Select
                placeholder='Set status'
                value={bulkStatus}
                onChange={(val) => setBulkStatus(val)}
                style={{ width: 180 }}
                size='small'
              >
                <Option value={true}>Blacklisted</Option>
                <Option value={false}>Not blacklisted</Option>
              </Select>
              <Button
                type='primary'
                size='small'
                loading={bulkUpdating}
                disabled={bulkStatus === null}
                onClick={handleBulkUpdate}
              >
                Apply
              </Button>
              <Button size='small' onClick={() => setSelectedKeys([])}>
                Clear selection
              </Button>
            </div>
          )}
          <Table
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: (keys) => setSelectedKeys(keys),
              getCheckboxProps: (record) => ({
                disabled: updatingStatus.has(record.key)
              })
            }}
            columns={columns}
            dataSource={tableData}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['20', '50', '100', '500', '1000']
            }}
            onChange={handleTableChange}
            scroll={{ x: 1400 }}
            size='middle'
            bordered
          />
        </div>
      </div>
    </div>
  );
};

export default DomainManagementPage;
